"use client";

import classNames from "classnames";
import Link from "next/link";
import { useState, useMemo } from "react";

import { sdkActionManager, useIsEmbed } from "@calcom/embed-core/embed-iframe";
import EventTypeDescription from "@calcom/web/modules/event-types/components/EventTypeDescription";
import { getOrgOrTeamAvatar } from "@calcom/lib/defaultAvatarImage";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { useRouterQuery } from "@calcom/lib/hooks/useRouterQuery";
import useTheme from "@calcom/lib/hooks/useTheme";
import { teamMetadataSchema } from "@calcom/prisma/zod-utils";
import { UserAvatarGroup } from "@calcom/ui/components/avatar";
import { Avatar } from "@calcom/ui/components/avatar";
import { Button } from "@calcom/ui/components/button";
import { UnpublishedEntity } from "@calcom/ui/components/unpublished-entity";

import { useToggleQuery } from "@lib/hooks/useToggleQuery";
import type { getServerSideProps } from "@lib/team/[slug]/getServerSideProps";
import type { inferSSRProps } from "@lib/types/inferSSRProps";

import Team from "@components/team/screens/Team";
import LocationPicker from "@components/team/screens/LocationPicker";
import AvailabilityExplorer from "@components/team/screens/AvailabilityExplorer";

import PoweredBy from "~/ee/common/components/PoweredBy";

export type PageProps = inferSSRProps<typeof getServerSideProps>;

type ConnectLocation = NonNullable<PageProps["connectLocations"]>[number];
type OrgEventType = NonNullable<PageProps["orgEventTypes"]>[number];

function TeamPage({
 team,
 considerUnpublished,
 isValidOrgDomain,
 connectLocations,
 orgEventTypes,
 selectedLocationSlug,
}: PageProps & { selectedLocationSlug?: string | null }) {
 useTheme(team.theme);
 const rawRouterQuery = useRouterQuery();
 const { subdomain: _subdomain, path: _path, ...routerQuery } = rawRouterQuery;
 const showMembers = useToggleQuery("members");
 const { t } = useLocale();
 const isEmbed = useIsEmbed();
 const teamName = team.name || t("nameless_team");
 const isBioEmpty = !team.bio || !team.bio.replace("<p><br></p>", "").length;
 const metadata = teamMetadataSchema.parse(team.metadata);

 const teamOrOrgIsPrivate = team.isPrivate || (team?.parent?.isOrganization && team.parent?.isPrivate);

 const locations = connectLocations ?? [];
 const hasLocations = locations.length > 0;

 const preselectedLocation = useMemo(() => {
 if (selectedLocationSlug) {
 return locations.find((l) => l.slug === selectedLocationSlug) ?? null;
 }
 if (locations.length === 1) {
 return locations[0];
 }
 return null;
 }, [selectedLocationSlug, locations]);

 const [selectedLocation, setSelectedLocation] = useState<ConnectLocation | null>(preselectedLocation);
 const [showLocationPicker, setShowLocationPicker] = useState(
 hasLocations && locations.length > 1 && !preselectedLocation
 );

 const allEventTypes = orgEventTypes && orgEventTypes.length > 0 ? orgEventTypes : team.eventTypes;

 const filteredEventTypes = useMemo(() => {
 if (!allEventTypes) return [];
 if (!selectedLocation) return allEventTypes;
 return allEventTypes.filter((et) => {
 // Check the multi-location join table first, fall back to connectLocationId
 const svcIds: number[] | undefined = (et as any).serviceLocationIds;
 if (Array.isArray(svcIds) && svcIds.length > 0) {
 return svcIds.includes(selectedLocation.id);
 }
 return (et as any).connectLocationId === selectedLocation.id;
 });
 }, [allEventTypes, selectedLocation]);

 if (considerUnpublished) {
 const teamSlug = team.slug || metadata?.requestedSlug;
 const parentSlug = team.parent?.slug || team.parent?.requestedSlug;
 const slugPropertyName = team.parent || team.isOrganization ? "orgSlug" : "teamSlug";
 return (
 <div className="flex h-full min-h-[calc(100dvh)] items-center justify-center">
 <UnpublishedEntity
 {...{ [slugPropertyName]: team.parent ? parentSlug : teamSlug }}
 logoUrl={team.parent?.logoUrl || team.logoUrl}
 name={team.parent ? team.parent.name : team.name}
 />
 </div>
 );
 }

 const { slug: _slug, orgSlug: _orgSlug, user: _user, subdomain: _sub, path: _p, ...queryParamsToForward } = routerQuery;

 const handleSelectLocation = (loc: ConnectLocation) => {
 setSelectedLocation(loc);
 setShowLocationPicker(false);
 };

 const handleBackToLocations = () => {
 setSelectedLocation(null);
 setShowLocationPicker(true);
 };

 const EventTypes = ({ eventTypes }: { eventTypes: typeof filteredEventTypes }) => (
 <ul className="border-subtle rounded-md border">
 {eventTypes.map((type, index) => {
 const locationQueryParams = selectedLocation
 ? {
 ...queryParamsToForward,
 "metadata[connectLocationId]": String(selectedLocation.id),
 "metadata[connectLocationName]": selectedLocation.name,
 ...(selectedLocation.address
 ? { "metadata[connectLocationAddress]": selectedLocation.address }
 : {}),
 ...(selectedLocation.city
 ? { "metadata[connectLocationCity]": selectedLocation.city }
 : {}),
 }
 : queryParamsToForward;

 const eventTeamSlug = "teamSlug" in type ? (type as OrgEventType).teamSlug ?? team.slug : team.slug;
 const eventTypePath = isValidOrgDomain
 ? `/${eventTeamSlug}/${type.slug}`
 : `/team/${eventTeamSlug}/${type.slug}`;

 return (
 <li
 key={index}
 className={classNames(
 "bg-default hover:bg-cal-muted border-subtle group relative border-b transition first:rounded-t-md last:rounded-b-md last:border-b-0",
 !isEmbed && "bg-default"
 )}>
 <div className="px-6 py-4 ">
 <Link
 prefetch={false}
 href={{
 pathname: eventTypePath,
 query: locationQueryParams,
 }}
 onClick={async () => {
 sdkActionManager?.fire("eventTypeSelected", {
 eventType: type,
 });
 }}
 data-testid="event-type-link"
 className="flex justify-between">
 <div className="shrink">
 <div className="flex flex-wrap items-center space-x-2 rtl:space-x-reverse">
 <h2 className=" text-default text-sm font-semibold">{type.title}</h2>
 </div>
 <EventTypeDescription className="text-sm" eventType={type} />
 </div>
 <div className="mt-1 self-center">
 <UserAvatarGroup truncateAfter={4} className="flex shrink-0" size="sm" users={type.users} />
 </div>
 </Link>
 </div>
 </li>
 );
 })}
 </ul>
 );

 const SubTeams = () =>
 team.children.length ? (
 <ul className="divide-subtle border-subtle bg-default static! w-full divide-y rounded-md border">
 {team.children.map((ch, i) => {
 const memberCount = team.members.filter(
 (mem) => mem.subteams?.includes(ch.slug) && mem.accepted
 ).length;
 return (
 <li key={i} className="hover:bg-cal-muted w-full rounded-md transition">
 <Link prefetch={false} href={`/${ch.slug}`} className="flex items-center justify-between">
 <div className="flex items-center px-5 py-5">
 <div className="ms-3 inline-block truncate">
 <span className="text-default text-sm font-bold">{ch.name}</span>
 <span className="text-subtle block text-xs">
 {t("number_member", {
 count: memberCount,
 })}
 </span>
 </div>
 </div>
 <UserAvatarGroup
 className="mr-6"
 size="sm"
 truncateAfter={4}
 users={team.members.filter((mem) => mem.subteams?.includes(ch.slug) && mem.accepted)}
 />
 </Link>
 </li>
 );
 })}
 </ul>
 ) : (
 <div className="stack-y-6" data-testid="event-types">
 <div className="overflow-hidden rounded-sm border-subtle border">
 <div className="text-muted p-8 text-center">
 <h2 className="font-cal text-emphasis mb-2 text-3xl">{` ${t("org_no_teams_yet")}`}</h2>
 <p className="text-emphasis mx-auto max-w-md">{t("org_no_teams_yet_description")}</p>
 </div>
 </div>
 </div>
 );

 const LocationContext = () => {
 if (!selectedLocation) return null;
 const addressParts = [selectedLocation.address, selectedLocation.city, selectedLocation.state, selectedLocation.zipCode]
 .filter(Boolean)
 .join(", ");
 return (
 <div className="border-subtle bg-default mb-4 flex items-center justify-between rounded-lg border px-4 py-3">
 <div className="flex items-center gap-3">
 <div className="bg-subtle flex h-8 w-8 items-center justify-center rounded-lg">
 <svg
 xmlns="http://www.w3.org/2000/svg"
 width="14"
 height="14"
 viewBox="0 0 24 24"
 fill="none"
 stroke="currentColor"
 strokeWidth="2"
 strokeLinecap="round"
 strokeLinejoin="round"
 className="text-emphasis">
 <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
 <circle cx="12" cy="10" r="3" />
 </svg>
 </div>
 <div>
 <p className="text-emphasis text-sm font-medium">{selectedLocation.name}</p>
 {addressParts && <p className="text-subtle text-xs">{addressParts}</p>}
 </div>
 </div>
 {locations.length > 1 && (
 <button
 type="button"
 onClick={handleBackToLocations}
 className="text-subtle hover:text-emphasis text-xs font-medium transition-colors">
 Change
 </button>
 )}
 </div>
 );
 };

 const profileImageSrc = getOrgOrTeamAvatar(team);

 return (
 <>
 <main className="dark:bg-default bg-subtle mx-auto max-w-3xl rounded-md px-4 pb-12 pt-12">
 <div className="mx-auto mb-8 max-w-3xl text-center">
 <div className="relative">
 <Avatar alt={teamName} imageSrc={profileImageSrc} size="lg" />
 </div>
 <p className="font-cal text-emphasis mb-2 text-2xl tracking-wider" data-testid="team-name">
 {team.parent && `${team.parent.name} `}
 {teamName}
 </p>
 {!isBioEmpty && (
 <>
 <div
 className=" text-subtle wrap-break-word text-sm [&_a]:text-blue-500 [&_a]:underline [&_a]:hover:text-blue-600"
 dangerouslySetInnerHTML={{ __html: team.safeBio }}
 />
 </>
 )}
 </div>
 {team.isOrganization ? (
 !teamOrOrgIsPrivate ? (
 showLocationPicker && hasLocations ? (
 <div>
 <LocationPicker
 locations={locations}
 onSelectLocation={handleSelectLocation}
 orgSlug={team.slug || ""}
 isValidOrgDomain={isValidOrgDomain ?? false}
 isEmbed={!!isEmbed}
 />
 {locations.length >= 2 && orgEventTypes && orgEventTypes.length > 0 && (
 <div className="mt-6">
 <div className="relative">
 <div className="absolute inset-0 flex items-center" aria-hidden="true">
 <div className="border-subtle w-full border-t" />
 </div>
 <div className="relative flex justify-center">
 <span className="bg-subtle text-subtle px-3 text-xs">or</span>
 </div>
 </div>
 <div className="mt-4">
 <AvailabilityExplorer
 locations={locations}
 orgEventTypes={orgEventTypes}
 orgTeamId={team.id}
 />
 </div>
 </div>
 )}
 </div>
 ) : hasLocations && selectedLocation ? (
 <div className="mx-auto max-w-3xl">
 <LocationContext />
 {filteredEventTypes.length > 0 ? (
 <>
 <EventTypes eventTypes={filteredEventTypes} />
 {locations.length >= 2 && orgEventTypes && orgEventTypes.length > 0 && (
 <div className="mt-4">
 <AvailabilityExplorer
 locations={locations}
 orgEventTypes={orgEventTypes}
 orgTeamId={team.id}
 />
 </div>
 )}
 {!team.hideBookATeamMember && !teamOrOrgIsPrivate && (
 <div>
 <div className="relative mt-12">
 <div className="absolute inset-0 flex items-center" aria-hidden="true">
 <div className="border-subtle w-full border-t" />
 </div>
 <div className="relative flex justify-center">
 <span className="bg-subtle text-subtle px-2 text-sm">{t("or")}</span>
 </div>
 </div>
 <aside className="dark:text-inverted mt-8 flex justify-center text-center">
 <Button
 color="minimal"
 EndIcon="arrow-right"
 data-testid="book-a-team-member-btn"
 className="dark:hover:bg-darkgray-200"
 href={{
 pathname: `${isValidOrgDomain ? "" : "/team"}/${team.slug}`,
 query: { ...queryParamsToForward, members: "1" },
 }}
 shallow={true}>
 {t("book_a_team_member")}
 </Button>
 </aside>
 </div>
 )}
 </>
 ) : (
 <div className="text-subtle py-8 text-center text-sm">
 No event types available at this location.
 </div>
 )}
 </div>
 ) : (
 <SubTeams />
 )
 ) : (
 <div className="w-full text-center">
 <h2 className="text-emphasis font-semibold">{t("you_cannot_see_teams_of_org")}</h2>
 </div>
 )
 ) : (
 <>
 {(showMembers.isOn || !team.eventTypes?.length) &&
 (teamOrOrgIsPrivate ? (
 <div className="w-full text-center">
 <h2 data-testid="you-cannot-see-team-members" className="text-emphasis font-semibold">
 {t("you_cannot_see_team_members")}
 </h2>
 </div>
 ) : (
 <Team members={team.members} teamName={team.name} />
 ))}
 {!showMembers.isOn && team.eventTypes && team.eventTypes.length > 0 && (
 <div className="mx-auto max-w-3xl ">
 <EventTypes eventTypes={team.eventTypes} />

 {!team.hideBookATeamMember && !teamOrOrgIsPrivate && (
 <div>
 <div className="relative mt-12">
 <div className="absolute inset-0 flex items-center" aria-hidden="true">
 <div className="border-subtle w-full border-t" />
 </div>
 <div className="relative flex justify-center">
 <span className="bg-subtle text-subtle px-2 text-sm">{t("or")}</span>
 </div>
 </div>

 <aside className="dark:text-inverted mt-8 flex justify-center text-center">
 <Button
 color="minimal"
 EndIcon="arrow-right"
 data-testid="book-a-team-member-btn"
 className="dark:hover:bg-darkgray-200"
 href={{
 pathname: `${isValidOrgDomain ? "" : "/team"}/${team.slug}`,
 query: {
 ...queryParamsToForward,
 members: "1",
 },
 }}
 shallow={true}>
 {t("book_a_team_member")}
 </Button>
 </aside>
 </div>
 )}
 </div>
 )}
 </>
 )}
 </main>
 {!team.hideBranding && (
 <div className="mx-auto max-w-3xl pb-4">
 <PoweredBy />
 </div>
 )}
 </>
 );
}

export default TeamPage;
